import { api } from "@/api/http";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useEffect, useMemo, useState } from "react";
import { X, Loader2, Wrench, Package } from "lucide-react";
import { formatBRLFromNumericString, parseBRLToNumericString, normalizeDecimalInput } from "@/utils/money";

type ItemTipo = "servico" | "peca";

type LookupPeca = {
  id: string;
  codigo?: string | null;
  descricao: string;
  marca?: string | null;
  unidade?: string | null;
  ncm?: string | null;
  precoVenda?: any;
  estoqueAtual?: any;
  estoqueMin?: any;
};

type Props = {
  open: boolean;
  osId: string;
  onClose: () => void;
  onCreated: () => void; // recarrega OS
};

function onlyNumbers(s: string) {
  return s.replace(/[^\d.,-]/g, "");
}

function isValidDecimal(s: string) {
  if (!s) return true;
  return /^-?\d+(\.\d+)?$/.test(s);
}

export default function AddItemModal({ open, osId, onClose, onCreated }: Props) {
  const [tipo, setTipo] = useState<ItemTipo>("servico");
  const [descricao, setDescricao] = useState("");
  const [qtd, setQtd] = useState("1");

  // valor (BRL mask)
  const [valorUnit, setValorUnit] = useState("");

  // peça
  const [pecaQ, setPecaQ] = useState("");
  const [pecaLoading, setPecaLoading] = useState(false);
  const [pecaResults, setPecaResults] = useState<LookupPeca[]>([]);
  const [pecaId, setPecaId] = useState<string>("");

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // reset básico ao abrir
    setTipo("servico");
    setDescricao("");
    setQtd("1");
    setValorUnit("");
    setPecaQ("");
    setPecaResults([]);
    setPecaId("");
    setSaving(false);
  }, [open]);

  const qtdN = useMemo(() => normalizeDecimalInput(qtd), [qtd]);

  const disabled = useMemo(() => {
    if (saving) return true;
    if (!qtdN || !isValidDecimal(qtdN)) return true;

    const vN = parseBRLToNumericString(valorUnit);
    if (vN && !isValidDecimal(vN)) return true;

    if (tipo === "servico") {
      if (!descricao.trim()) return true;
      return false;
    }

    // peça
    if (!pecaId) return true;
    return false;
  }, [saving, tipo, descricao, qtdN, valorUnit, pecaId]);

  async function searchPecas(query: string) {
    const q = query.trim();
    if (!q) {
      setPecaResults([]);
      return;
    }
    setPecaLoading(true);
    try {
      const { data } = await api.get("/api/pecas/lookup", { params: { q, limit: 10 } });
      setPecaResults(data?.data ?? []);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Erro ao buscar peças");
    } finally {
      setPecaLoading(false);
    }
  }

  async function onSubmit() {
    if (disabled) return;

    const payload: any = {
      tipo,
      qtd: qtdN ? qtdN : "1",
    };

    const vN = parseBRLToNumericString(valorUnit);
    if (vN) payload.valorUnit = vN;

    if (tipo === "servico") {
      payload.descricao = descricao.trim();
    } else {
      payload.pecaId = pecaId;
      // descrição é obrigatória no schema, mas no backend você usa fallback pra descrição da peça.
      payload.descricao = "Peça";
    }

    setSaving(true);
    try {
      await api.post(`/api/os/${osId}/itens`, payload);
      toast.success("Item adicionado ✅");
      onCreated();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Erro ao adicionar item";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={saving ? undefined : onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl rounded-xl bg-white shadow-lg border border-slate-200">
          {/* header */}
          <div className="flex items-start justify-between gap-3 p-4 border-b border-slate-200">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Adicionar item</h3>
              <p className="text-sm text-slate-600 mt-1">Serviço ou peça. Se for peça, baixa estoque automaticamente.</p>
            </div>

            <button onClick={saving ? undefined : onClose} className="p-1 rounded hover:bg-slate-100" aria-label="Fechar">
              <X size={18} />
            </button>
          </div>

          {/* body */}
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-1">
                <label className="text-sm font-medium text-slate-700">Tipo</label>
                <select
                  value={tipo}
                  onChange={(e) => {
                    setTipo(e.target.value as any);
                    setPecaId("");
                    setPecaResults([]);
                    setPecaQ("");
                  }}
                  className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="servico">Serviço</option>
                  <option value="peca">Peça</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Quantidade</label>
                <Input value={qtd} onChange={(e) => setQtd(onlyNumbers(e.target.value))} placeholder="1" inputMode="decimal" />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Valor unitário (opcional)</label>
                <Input
                  value={valorUnit}
                  onChange={(e) => setValorUnit(e.target.value)}
                  onFocus={() => {
                    const raw = parseBRLToNumericString(valorUnit);
                    setValorUnit(raw ? raw.replace(".", ",") : "");
                  }}
                  onBlur={() => {
                    const raw = parseBRLToNumericString(valorUnit);
                    setValorUnit(raw ? formatBRLFromNumericString(raw) : "");
                  }}
                  placeholder="R$ 0,00"
                  inputMode="decimal"
                />
              </div>
            </div>

            {tipo === "servico" ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Wrench size={16} /> Descrição do serviço *
                </label>
                <Input value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex: Troca de óleo + filtro" />
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Package size={16} /> Selecionar peça *
                </label>

                <div className="flex gap-2">
                  <Input
                    value={pecaQ}
                    onChange={(e) => setPecaQ(e.target.value)}
                    placeholder="Buscar por código, descrição, marca..."
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-2"
                    disabled={pecaLoading || !pecaQ.trim()}
                    onClick={() => searchPecas(pecaQ)}
                  >
                    {pecaLoading ? <Loader2 className="animate-spin" size={16} /> : "Buscar"}
                  </Button>
                </div>

                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  {pecaResults.length === 0 ? (
                    <div className="p-3 text-sm text-slate-500">
                      Busque e selecione uma peça.
                    </div>
                  ) : (
                    <div className="divide-y max-h-64 overflow-auto">
                      {pecaResults.map((p) => {
                        const selected = pecaId === p.id;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setPecaId(p.id)}
                            className={`w-full text-left p-3 hover:bg-slate-50 transition ${selected ? "bg-slate-50" : ""}`}
                          >
                            <div className="font-medium text-slate-900">
                              {p.codigo ? `${p.codigo} • ` : ""}{p.descricao}
                            </div>
                            <div className="text-xs text-slate-500">
                              {p.marca ? `Marca: ${p.marca} • ` : ""}
                              Estoque: {String(p.estoqueAtual ?? "0")}
                              {p.precoVenda !== undefined ? ` • Venda: ${String(p.precoVenda)}` : ""}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* footer */}
          <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={onSubmit} disabled={disabled} className="gap-2">
              {saving ? <Loader2 className="animate-spin" size={16} /> : null}
              Adicionar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
